import { ChangeEvent, FC, useCallback, useRef, useState } from 'react';
import { Button, TypeSelector } from '@features/ui';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { ProductFormData, TradingPlatForms } from '@src/types';
import { companyData } from '@features/admin/productsAddons/productsAddonsData.ts';
import { useTradovateTemplateStore } from '@stores/tradovateTemplateStore.ts';

interface ProductInstrumentsFormProps {
  disabled: boolean;
}
export const ProductInstrumentsForm: FC<
  UseFormReturn<ProductFormData> & ProductInstrumentsFormProps
> = ({ control, setValue, disabled }) => {
  const [fileStandardName, setFileStandardName] = useState('');
  const [filePerformanceName, setFilePerformanceName] = useState('');

  const fileInputRef1 = useRef<HTMLInputElement>(null);
  const fileInputRef2 = useRef<HTMLInputElement>(null);

  const { tradovateOptions } = useTradovateTemplateStore();
  const type = useWatch({ control, name: 'company' });

  const handleButtonClick = (type: 'standard' | 'performance') => {
    if (type === 'standard') {
      fileInputRef1.current?.click();

      return;
    }
    fileInputRef2.current?.click();
  };

  // Read CSV file content and convert to string
  const readCSVContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const text = e.target?.result as string;
        const normalized = text.trim().replace(/\r\n/g, '\n');

        resolve(normalized);
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<ProductFormData, keyof ProductFormData>
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      const csvContent = await readCSVContent(file);

      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const uuid = crypto.randomUUID();
      const filePath = `${year}/${month}/${day}/${uuid}.csv`;

      console.log(csvContent);

      if (field.name === 'rithmic_standard_package') {
        setFileStandardName(file.name);
        field.onChange(filePath);
        setValue('package_rms_content_standard', csvContent);
      } else if (field.name === 'rithmic_performance_package') {
        setFilePerformanceName(file.name);
        field.onChange(filePath);
        setValue('package_rms_content_performance', csvContent);
      }
    } catch (error) {
      console.error('Error reading CSV file:', error);
    }
  };

  const renderCompany = useCallback(() => {
    return (
      <>
        {/* RITHMIC_DATA_FEED */}
        <div
          className={'lg:flex-row flex-col mt-10 gap-[81px] w-full'}
          style={{
            display:
              type === TradingPlatForms.RITHMIC_DATA_FEED ? 'flex' : 'none',
          }}
        >
          <Controller
            control={control}
            name="rithmic_standard_package"
            render={({ field }) => {
              return (
                <div className={'flex flex-0.5 flex-col gap-2 ml-0 w-fit'}>
                  <p>Standard Package</p>
                  <p>Upload CSV file</p>
                  <div className={'flex gap-2 items-end'}>
                    <Button
                      disabled={disabled}
                      size={'sm'}
                      type="button"
                      variant={'black'}
                      onClick={() => handleButtonClick('standard')}
                    >
                      Browse File
                    </Button>
                    <p>{fileStandardName ?? 'No file chosen'}</p>
                    <input
                      ref={fileInputRef1}
                      accept=".csv"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(e) => handleFileChange(e, field)}
                    />
                  </div>
                </div>
              );
            }}
          />
          <Controller
            control={control}
            name="rithmic_performance_package"
            render={({ field }) => {
              return (
                <div
                  className={
                    'flex flex-0.5 flex-col gap-2 lg:-ml-[21px] ml-0 w-fit'
                  }
                >
                  <p>Performance Package</p>
                  <p>Upload CSV file</p>
                  <div className={'flex gap-2 items-end'}>
                    <Button
                      disabled={disabled}
                      size={'sm'}
                      type="button"
                      variant={'black'}
                      onClick={() => handleButtonClick('performance')}
                    >
                      Browse File
                    </Button>
                    <p>{filePerformanceName ?? 'No file chosen'}</p>
                    <input
                      ref={fileInputRef2}
                      accept=".csv"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(e) => handleFileChange(e, field)}
                    />
                  </div>
                </div>
              );
            }}
          />
        </div>

        {/* CQG_DATA_FEED (Tradovate) - Commented out */}
        {/* <div
          className={'w-full mt-10 max-w-[500px]'}
          style={{
            display: type === TradingPlatForms.CQG_DATA_FEED ? 'block' : 'none',
          }}
        >
          <Controller
            control={control}
            name="tradovate_financial_template"
            render={({ field }) => (
              <div className={'space-y-2'}>
                <Label htmlFor={field.name} size={'xs'}>
                  Tradovate Financial Template
                </Label>
                <InputSelect
                  disabled={disabled}
                  options={tradovateOptions}
                  placeholder={'Enter'}
                  value={field.value}
                  onChange={field.onChange}
                />
              </div>
            )}
          />
        </div> */}

        {/* DX_DATA_FEED - Commented out */}
        {/* <div
          className={'w-full mt-10 max-w-[500px]'}
          style={{
            display: type === TradingPlatForms.DX_DATA_FEED ? 'block' : 'none',
          }}
        >
          <Controller
            control={control}
            name="dxfeed_financial_template"
            render={({ field }) => (
              <div className={'space-y-2'}>
                <Label htmlFor={field.name} size={'xs'}>
                  dxFeed Financial Template
                </Label>
                <Input
                  {...field}
                  placeholder={'#'}
                  textSize={'xs'}
                  variant={'secondary'}
                />
              </div>
            )}
          />
        </div> */}
      </>
    );
  }, [
    type,
    control,
    disabled,
    fileStandardName,
    filePerformanceName,
    tradovateOptions,
  ]);

  return (
    <div
      className={
        'text-xs mb-[33px] border-dotted border-[#FFFFFF] border-t-[1.5px]'
      }
    >
      <div className={'mt-[26px] lg:ml-10 ml-5'}>
        <div className={'w-fit flex flex-col gap-[5px]'}>
          <p className={'text-[16px]'}>Instruments</p>
          <div className={'w-full h-[1px] bg-white'} />
        </div>
        <div
          className={
            'flex flex-col xl:w-[80%] w-full mt-[30px] gap-x-[38px] gap-y-7'
          }
        >
          <Controller
            control={control}
            name="company"
            render={({ field }) => {
              const indexOfType = companyData.findIndex(
                (option) => option.value === field.value
              );

              return (
                <div className={'w-fit'}>
                  <TypeSelector
                    disabled={disabled}
                    tabActive={indexOfType >= 0 ? indexOfType : 0}
                    tabs={companyData}
                    onChangeTab={(_, item) => field.onChange(item.value)}
                  />
                  <div
                    className={'flex-1 bg-white origin-top'}
                    style={{
                      height: '1px',
                      transform: 'scaleY(0.3)',
                    }}
                  />
                </div>
              );
            }}
          />
          {renderCompany()}
        </div>
      </div>
    </div>
  );
};
